// packages/web/src/pages/teach/HomeworkDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHomeworkStore } from '../../stores/homeworkStore';
import { useBasePath } from '../../contexts/BasePathContext';
import { ArrowLeft, Calendar, BookOpen, Users, Edit } from 'lucide-react';
import { StudentProgressTable } from '../../components/teach/StudentProgressTable';
import { EditHomeworkModal } from '../../components/teach/EditHomeworkModal';

export const HomeworkDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { basePath } = useBasePath();
  const { currentHomework, homeworkProgress, loading, fetchHomeworkDetail, fetchHomeworkProgress, updateHomework } = useHomeworkStore();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchHomeworkDetail(id);
      fetchHomeworkProgress(id);
    }
  }, [id]);

  if (loading || !currentHomework) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  const isOverdue = new Date(currentHomework.due_date) < new Date();
  const completionRate = homeworkProgress?.completion_rate || 0;

  const handleSaveEdit = async (updates: { name?: string; description?: string; due_date?: string }) => {
    if (!id) return;
    await updateHomework(id, updates);
    await fetchHomeworkDetail(id);
  };

  return (
    <div className="homework-detail-page min-h-screen bg-gray-50 p-6">
      {/* 头部 */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`${basePath}/homeworks`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          返回作业列表
        </button>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{currentHomework.name}</h1>
              {currentHomework.description && (
                <p className="text-gray-600">{currentHomework.description}</p>
              )}
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <span className={`
                px-3 py-1 text-sm font-medium rounded-full
                ${isOverdue ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
              `}>
                {isOverdue ? '已截止' : '进行中'}
              </span>
              
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Edit className="w-4 h-4" />
                编辑
              </button>
            </div>
          </div>

          {/* 基本信息 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">科目</p>
                <p className="text-sm font-medium text-gray-900">
                  {getCategoryLabel(currentHomework.subject)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">截止日期</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(currentHomework.due_date).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">学生人数</p>
                <p className="text-sm font-medium text-gray-900">
                  {homeworkProgress?.total_students || 0} 名学生
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 完成情况统计 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-500 mb-1">总学生数</p>
          <p className="text-3xl font-bold text-gray-900">{homeworkProgress?.total_students || 0}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-500 mb-1">已完成</p>
          <p className="text-3xl font-bold text-green-600">{homeworkProgress?.completed_students || 0}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-500 mb-1">未完成</p>
          <p className="text-3xl font-bold text-orange-600">
            {(homeworkProgress?.total_students || 0) - (homeworkProgress?.completed_students || 0)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-500 mb-1">完成率</p>
          <p className="text-3xl font-bold text-blue-600">{completionRate}%</p>
        </div>
      </div>

      {/* 习题列表 */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          作业习题 ({currentHomework.exercises?.length || 0})
        </h2>
        
        {currentHomework.exercises && currentHomework.exercises.length > 0 ? (
          <div className="space-y-3">
            {currentHomework.exercises.map((exercise, index) => (
              <div key={exercise.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{exercise.title}</p>
                  {exercise.teacher_requirement && (
                    <p className="text-xs text-gray-500 mt-1">教学要求: {exercise.teacher_requirement}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">暂无习题</p>
        )}
      </div>

      {/* 布置对象 */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">布置对象</h2>
        
        {currentHomework.assignments && currentHomework.assignments.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {currentHomework.assignments.map((assignment, index) => (
              <span
                key={index}
                className={`
                  inline-flex items-center px-3 py-1 rounded-full text-sm
                  ${assignment.assignee_type === 'class'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-blue-100 text-blue-800'
                  }
                `}
              >
                {assignment.assignee_type === 'class' ? '班级: ' : '学生: '}
                {assignment.assignee_name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">暂无布置对象</p>
        )}
      </div>

      {/* 学生完成情况 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">学生完成情况</h2>
        <StudentProgressTable />
      </div>

      {/* 编辑对话框 */}
      <EditHomeworkModal
        homework={currentHomework}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
      />
    </div>
  );
};
